import { useLanguage, useMessageActionContext } from '@/app/providers';
import { StarIcon } from '@/assets/images';
import { Checkbox, CustomButton } from '@/components';
import MessageActionDropdown from '@/features/chats/ui/Conversation/components/MessageActionDropdown';
import { useMessageAction } from '@/features/chats/viewmodels/useMessageAction';
import { formatChatTime } from '@/features/timeformatter/timeStamp';
import { Username, UserProfile } from '@/features/users';
import { getLocalUserId, getMessageSenderId } from '@/shared/utils';
import { isGroupChat } from '@/features/chats/utils/jidIdFormat';
import PropTypes from 'prop-types';
import { memo, useMemo } from 'react';
import MessageStatus from '../MessageStatus';
import ReplyMessageCard from '../ReplyMessageCard';

const SENDER_CLASS = 'sender-chat chat-bubble ct-flex ct-rowReverse ct-aiEnd ct-gap10 ct-padding-lr8-rl16';
const RECEIVER_CLASS = 'receiver-chat chat-bubble ct-flex ct-gap10 ct-padding-lr8-rl16';

const ContactCard = memo(({ message, activeChatJid }) => {
   const { t } = useLanguage();
   const { hideActions = false, hideAvatar = false } = useMessageActionContext();
   const { isSelected, isSelectionActive, handleToggleSelection } = useMessageAction(message, activeChatJid);
   const senderId = getMessageSenderId({ type: 'chats', message });
   const isSender = senderId === getLocalUserId();
   const contact = message.msgBody?.contact ?? {};
   const contactName = contact?.name || t('common.UNKNOWN_CONTACT');
   const contactNumber = contact?.phone_number[0];

   const timestamp = useMemo(
      () => (message.timestamp ? formatChatTime(new Date(Number(message.timestamp)), 'message-card') : ''),
      [message.timestamp],
   );
   const isChatGroup = useMemo(() => isGroupChat(activeChatJid), [activeChatJid]);
   const hideAvatarContactCard = isSender || hideAvatar;
   const showProfile = isChatGroup && !hideAvatarContactCard;
   const isHighlighted = message?.isHighlighted;

   return (
      <CustomButton
         as="div"
         className={`message-row contact-card ${isSelectionActive && !hideActions ? 'cts-contact ct-flex ct-aiCenter selection-active' : ''} ${isSelected && !hideActions ? 'selected' : ''} ${isHighlighted && !hideActions ? 'search-highlight-active' : ''}`}
         onClick={isSelectionActive && !hideActions ? handleToggleSelection : null}
      >
         {isSelectionActive && !hideActions && (
            <div className="selection-checkbox-wrap cts-contact">
               <Checkbox checked={isSelected} onChange={handleToggleSelection} />
            </div>
         )}
         <div className={`${isSender ? SENDER_CLASS : RECEIVER_CLASS}`}>
            {showProfile && (
               <picture className="chat-avatar cts-contact">
                  <UserProfile userId={senderId} className="chat-avatar chat-avatar-fallback ct-flexCenter" />
               </picture>
            )}

            <div className={`ct-flex ct-relative ${isSender ? 'ct-flexColEnd' : 'ct-flexCol'} ct-gap4`}>
               <div className="chat-box contact-card-box ct-flex ct-aiCenter ct-gap12">
                  {showProfile && <Username userId={senderId} />}
                  <ReplyMessageCard replyTo={message.msgBody?.replyTo} activeChatJid={activeChatJid} />
                  <div className="contact-info-wrap ct-flex ct-aiCenter ct-gap12">
                     <div className="contact-avatar-display ct-flexCenter">
                        <UserProfile userId={null} className="contact-avatar-img ct-flexCenter" />
                     </div>
                     <div className="contact-text ct-flex ct-fcol ct-gap4">
                        <span className="contact-displayName ct-text-white">{contactName}</span>
                        <span className="contact-displayNumber ct-text-white">{contactNumber}</span>
                     </div>
                  </div>
               </div>

               <div className={`cts-contact ct-flexAlign ct-flex ct-gap8 ${isSender ? 'ct-rowReverse' : ''}`}>
                  {isSender && <MessageStatus msgStatus={message.msgStatus} />}
                  <span className="time cts-contact">{timestamp}</span>
                  {message.editedStatus === 1 && <span className="edited-icon">{t('message.EDITED')}</span>}
                  {message.favouriteStatus === 1 && (
                     <i className="icon starIcon ct-flexCenter">
                        <StarIcon />
                     </i>
                  )}
               </div>

               {!hideActions && (
                  <MessageActionDropdown
                     className="cts-contact"
                     message={message}
                     isUser={isSender}
                     activeChatJid={activeChatJid}
                  />
               )}
            </div>
         </div>
      </CustomButton>
   );
});

ContactCard.displayName = 'ContactCard';

ContactCard.propTypes = {
   message: PropTypes.object.isRequired,
   activeChatJid: PropTypes.string,
};

export default ContactCard;
